import { useTranslation } from "react-i18next";
import { useFetchWrapper } from "../hooks/useFetchWrapper";
import { useLoading } from "../utils/LoadingContext";
import {
    AUTHORIZATION_TYPE,
    GRADED_POLL,
    POLL_SHARE_MODE,
    POLL_TYPE
} from "../utils/constants";
import {
    formatAPIErrorLog,
    formatAPIResponseLog,
    getShortName,
    notifyError,
    notifySuccess,
    notifyWarning
} from "../utils/helpers";
import {setMHOptionsMap} from "../utils/questionUtils";
import {useConfig} from "../hooks/useConfig";

export const PollApiCalls = () => {
    const { t } = useTranslation();

    const fetchWrapper = useFetchWrapper();
    let config = useConfig();
    const { setLoading } = useLoading();
    return {
        uploadQueImage,
        uploadOptImage,
        uploadQueBankImage,
        deleteImage,
        duplicateImagesOfQuestion,
        duplicateImagesOfQuestionBank,
        publishPoll,
        saveAndPublish,
        updateAndPublish,
        reactivatePoll,
        sharePollToUsers,
        copyPollToUser, 
        softDeleteDraftPoll,
        softDeletePublishedPoll,
        shareQuestionPresentation,
        shareQuestion,
        shareAllQuestions,
        stopQuestion,
        stopQuestionPresentation,
        stopAllQuestions,
        stopScheduledPoll,
        lockQuestion,
        pauseQuestion,
        resumeQuestion,
        getPollsFromUser,
        getDeletedPollsFromUser,
        restorePolls,
        hardDeletePolls,
        getPollsFromCourse,
        getAttendancesFromCourse,
        getPollDataFromKey,
        getPublishedPollDataFromKey,
        getPollRecordFromUniqueCode,
        getActivePollsFromUser,
        getActivePollsFromCourse,
        importBankImage,
        editPublishedPollDataFromKey,
        getUploadLinkForVideo,
        uploadVideo,
        deleteVideo,
        fakeCall,
        getPollsPPT,
        updatePollDetails,
        updateQuestionDetails,
        addQuestionToLivePoll,
        getPollDataFromKeyPPT,
        // notifySchedule
    }




    // Function to save the uploaded image temporarily
    // till the poll is saved returns the url of the image
    function uploadQueImage(pollKey, imgData, setUpFunc) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/upload/queImg`,
            body: { pollKey, fileName: imgData.name }
        }).then(result => {
            return result;
        }).then(async ({putURL: url, getURL}) =>{
            if (!url) {
                return "";
            }
            const res = await fetch(url, {
                headers: {"Content-Type": imgData.type},
                method: 'PUT',
                body: imgData
            });
            if (setUpFunc) {
                await setUpFunc(imgData.name, getURL);
            }
            return res;
        }).catch((error) => {
            notifyError(t('institution.uploadImageError'));
            console.log(error);
            return null;
        })
    }

    // Function to save the uploaded image temporarily
    // till the poll is saved returns the url of the image
    function uploadOptImage(pollKey, imgData, setUpFunc) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/upload/optImg`,
            body: { fileName: imgData.name, pollKey}
        }).then(result => {
            return result;
        }).then(async ({putURL: url, getURL}) =>{
            if (!url) {
                return "";
            }
            const res = await fetch(url, {
                headers: {"Content-Type": imgData.type},
                method: 'PUT',
                body: imgData
            });
            if (setUpFunc) {
                setUpFunc(imgData.name, getURL);
            }
            return res;
        }).catch((error) => {
            notifyError(t('institution.uploadImageError'));
            console.log(error);
            return null;
        });
    }


    function uploadQueBankImage(questionBankId, imgData, setUpFunc) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/upload/queBankImg`,
            body: { fileName: imgData.name, questionBankId}
        }).then(result => {
            return result;
        }).then(async ({putURL: url, getURL}) =>{
            if (!url) {
                return "";
            }
            const res = await fetch(url, {
                headers: {"Content-Type": imgData.type},
                method: 'PUT',
                body: imgData
            });
            if (setUpFunc) {
                setUpFunc(imgData.name, getURL);
            }
            return res;
        }).catch((error) => {
            notifyError(t('institution.uploadImageError'));
            console.log(error);
            return null;
        });
    }

    function duplicateImagesOfQuestion(pollKey, selectedQuestion, resourceMapping) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/duplicate/img`,
            body: { pollKey, selectedQuestion, resourceMapping }
        }).then(result => {
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.duplicateImageError'));
            console.log(error);
            return null;
        })
    }

    function duplicateImagesOfQuestionBank(questionBankId, resourceMapping) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/duplicate/img`,
            body: { questionBankId, resourceMapping }
        }).then(result => {
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.duplicateImageError'));
            console.log(error);
            return null;
        })
    }

    // Function to delete the uploaded image
    // returns void
    function deleteImage(loc) {
        setLoading(true);
        return fetchWrapper.delete({
            url: `${config.api.poll}/poll/delete`,
            body: { loc }
        }).then((result) => {
            console.log(result);
        }).catch((error) => {
            // notifyError(DELETE_IMAGE_ERROR);
            console.log(error);
            return null;
        }
        ).finally(() => {
            setLoading(false);
        });
    }

    // Function to get upload link from evp for video
    function getUploadLinkForVideo() {
        return fetchWrapper.get({
            url: `${config.api.poll}/poll/upload/video`,
        }).then((result) => {
            return result.url;
        }).catch((error) => {
            console.log(error);
            return null;
        }).finally(() => {
        });
    }

    // Function to upload video to evp and return direct link
    function uploadVideo(fileKey, title, pollKey, serialNo) {
        return fetchWrapper.post({
            url: `${config.api.poll}/poll/upload/video`,
            body: {
                fileKey: fileKey,
                title: title,
                pollKey: pollKey,
                serialNo: serialNo
            }
        }).then((result) => {
            return result.url;
        }).catch((error) => {
            notifyError(t('questionBuilder.uploadVideoError'));
            console.log(error);
            return null;
        });
    }

    // Function to delete the uploaded image
    // Returns success if successful
    function deleteVideo(pollKey, serialNo, showLoading = false) {
        setLoading(true);
        return fetchWrapper.delete({
            url: `${config.api.poll}/poll/delete/video`,
            body: { pollKey, serialNo}
        }).then((result) => {
            return result.response;
        }).catch((error) => {
            console.log(error);
            return null;
        }).finally(() => {
            setLoading(false);
        });
    }




    // Function to soft delete a draft poll
    // returns void
    function softDeleteDraftPoll(pollKey, pollType, showToast=true) {
        const bodyObj = { pollKey: pollKey, };
        setLoading(true);
        return fetchWrapper.delete({
            url: `${config.api.poll}/poll/draft`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            if (showToast) {
                notifySuccess(pollType === POLL_TYPE.ATTENDANCE ? t('common.deleteSuccess', { entity: t('entities.draftAttendance') }) : t('common.deleteSuccess', { entity: t('entities.draftPoll') }));
            }
            return result;
        }).catch((error) => {
            if (showToast) {
                notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('common.deleteError', { entity: t('entities.draftAttendance') }) : t('common.deleteError', { entity: t('entities.draftPoll') }));
            }
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to soft delete a published poll
    // returns void
    function softDeletePublishedPoll(pollKey, pollType) {
        const bodyObj = { pollKey: pollKey};
        setLoading(true);
        return fetchWrapper.delete({
            url: `${config.api.poll}/poll/published`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(pollType === POLL_TYPE.ATTENDANCE ? t('common.deleteSuccess', { entity: t('entities.publishedAttendance') }) : t('common.deleteSuccess', { entity: t('entities.publishedPoll') }));
            return result;
        }).catch((error) => {
            notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('common.deleteError', { entity: t('entities.publishedAttendance') }) : t('common.deleteError', { entity: t('entities.publishedPoll') }));
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    function hardDeletePolls(pollKeys, pollCodes, showToast=true, singleDeleteType=null) {
        const bodyObj = { pollKey: pollKeys, pollCode: pollCodes};
        setLoading(true);
        return fetchWrapper.delete({
            url: `${config.api.poll}/poll/hardDelete`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            if (showToast && singleDeleteType) {
                notifySuccess(singleDeleteType === POLL_TYPE.ATTENDANCE ? t('common.deleteSuccess', { entity: t('entities.attendance') }) : t('poll.deleteMultipleSuccess'));
            } else if (showToast) {
                notifySuccess(t('poll.deleteMultipleSuccess'));
            }
            return result;
        }).catch((error) => {
            if (showToast && singleDeleteType) {
                notifyError(singleDeleteType === POLL_TYPE.ATTENDANCE ? t('common.deleteError', { entity: t('entities.attendance') }) : t('poll.deleteMultipleError'));
            } else if (showToast) {
                notifyError(t('poll.deleteMultipleError'));
            }
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    function restorePolls(pollKeys, pollCodes, showToast=true, singleRestoreType=null) {
        const bodyObj = { pollKey: pollKeys, pollCode: pollCodes};
        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/restore`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            if (showToast && singleRestoreType) {
                notifySuccess(singleRestoreType === POLL_TYPE.ATTENDANCE ? t('common.restoreSuccess', { entity: t('entities.attendance') }) : t('common.restoreSuccess', { entity: t('entities.poll') }));
            } else if (showToast) {
                notifySuccess(t('poll.restoreMultipleSuccess'));
            }
            return result;
        }).catch((error) => {
            if (showToast && singleRestoreType) {
                notifyError(singleRestoreType === POLL_TYPE.ATTENDANCE ? t('common.restoreError', { entity: t('entities.attendance') })  : t('common.restoreError', { entity: t('entities.poll') }));
            } else if (showToast) {
                notifyError(t('poll.restoreMultipleError'));
            }
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to publish a poll, this creates a uniqueCode and deletes the draft poll
    // returns the pollKey of the poll, the uniqueCode and the expirationTime
    function publishPoll(pollToAddBody, isSilent=false, spinner=true) {
        const bodyObj = pollToAddBody;
        if (spinner) {
            setLoading(true);
        }
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/saveAndPublish`,
            body: bodyObj
        }).then((result) => {
            if (result.uniqueCode) {
                return result;
            } else if(!isSilent) {
                notifyWarning(t('poll.missingUniqueCode'));
            }
            return null;
        }).catch((error) => {
            if (!isSilent) {
                const {pollTitle, pollType=GRADED_POLL} = pollToAddBody;
                if (error.message && JSON.parse(error.message).errorMessage === "Incomplete Poll") {
                    notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('attendance.incompleteError') : t('poll.incompleteError'));
                } else {
                    notifyError(t('poll.releaseError', { pollTitle }));
                }
            }
            console.log(error);
            return null;
        }).finally(() => {
            if (spinner) {
                setLoading(false);
            }
        });
    }


    function saveAndPublish(pollToAddBody, normalSave=true) {
        const {pollTitle, questions=[]} = pollToAddBody;
        setMHOptionsMap(questions);
        pollToAddBody.isAutomatic = !normalSave;
        pollToAddBody.saveRequired = true;
        if (normalSave) {
            setLoading(true);
        }
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/saveAndPublish`,
            body: pollToAddBody
        }).then((result) => {
            if (normalSave) {
                notifySuccess(t('poll.saveSuccess', { pollTitle }));
            }
            return result;
        }).catch((error) => {
            if (normalSave) {
                notifyError(t('poll.saveError', { pollTitle }));
            }
            console.log(error);
            return null;
        }).finally(() => {
            if (normalSave) {
                setLoading(false);
            }
        });
    }


    function updateAndPublish(pollToAddBody, normalSave=true, hasReleased=false) {
        const {pollTitle, questions=[]} = pollToAddBody;
        setMHOptionsMap(questions);
        if (normalSave) {
            setLoading(true);
        }
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/saveAndPublish`,
            body: {...pollToAddBody, isAutomatic: !normalSave, saveRequired: true, hasReleased}
        }).then((result) => {
            if (normalSave) {
                notifySuccess(t('poll.saveSuccess', { pollTitle }));
            }
            return result;
        }).catch((error) => {
            if (normalSave) {
                notifyError(t('poll.saveError', { pollTitle }));
            }
            console.log(error);
            return null;
        }).finally(() => {
            if (normalSave) {
                setLoading(false);
            }
        });
    }

    // Function to reactivate a poll with new share mode and new course, this creates a new poll
    // returns the pollKey of the poll, the uniqueCode and the expirationTime
    function reactivatePoll(pollToAddBody) {
        console.log(pollToAddBody);
        const bodyObj = pollToAddBody;
        const {pollTitle} = pollToAddBody;
        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/reactivate`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            return result;
        }).catch((error) => {
            notifyError(t('poll.reactivateError', { pollTitle }));
            console.log(error);
            return null;
        }).finally(() => {
            setLoading(false);
        });
    }

    
    // Function to share a poll to different users within the institution
    // returns the pollKeys of the new polls
    function sharePollToUsers(pollToAddBody) {
        console.log(pollToAddBody);
        const bodyObj = pollToAddBody;
        const {pollType = GRADED_POLL} = bodyObj;
        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/share/users`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(pollType === POLL_TYPE.ATTENDANCE ? t('common.shareSuccess', { entity: t('entities.attendance') }) : t('common.shareSuccess', { entity: t('entities.poll') }));
            return result;
        }).catch((error) => {
            notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('common.shareError', { entity: t('entities.attendance') }) : t('common.shareError', { entity: t('entities.poll') }));
            console.log(error);
            return null;
        }).finally(() => {
            setLoading(false);
        });
    }

    function copyPollToUser(pollToAddBody) {
        console.log(pollToAddBody);
        const bodyObj = pollToAddBody;
        const {pollType = GRADED_POLL} = bodyObj;
        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/copy`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            if (pollType === POLL_TYPE.MERGED_POLL) {
                notifySuccess(t('common.copySuccess', { entity: t('entities.poll') }));
            } else if (pollType === POLL_TYPE.ATTENDANCE) {
                notifySuccess(t('common.copySuccess', { entity: t('entities.attendance') }));
            } else {
                notifySuccess(t('poll.copySuccessEitherType'));
            }
            return result;
        }).catch((error) => {
            if (pollType === POLL_TYPE.MERGED_POLL) {
                notifySuccess(t('common.copyError', { entity: t('entities.poll') }));
            } else if (pollType === POLL_TYPE.ATTENDANCE) {
                notifySuccess(t('common.copyError', { entity: t('entities.attendance') }));
            } else {
                notifySuccess(t('poll.copyEitherTypeError'));
            }
            console.log(error);
            return null;
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to share one specific question from a poll
    // returns liveQuestionTime
    function shareQuestion(pollKey, uniqueCode, serialNo, pollType=POLL_TYPE.MERGED_POLL) {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
            serialNo: serialNo,
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/share`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(pollType === POLL_TYPE.ATTENDANCE ? t('attendance.releaseSuccess') : t('questionBuilder.releaseQuestionSuccess', { serialNo }));
            return result;
        }).catch((error) => {
            notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('attendance.releaseError') : t('questionBuilder.releaseQuestionError', { serialNo }));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to share one specific question from a poll
    // returns liveQuestionTime
    function shareQuestionPresentation(pollKey, uniqueCode, serialNo, pollType, token='') {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
            serialNo: serialNo,
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/share`,
            body: bodyObj,
            token: token
        }).then((result) => {
            console.log(result);
            return result;
        }).catch((error) => {
            notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('attendance.releaseError') : t('questionBuilder.releaseQuestionError', { serialNo }));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    function shareAllQuestions(pollKey, uniqueCode) {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/share-all`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(t('poll.releaseSuccess'));
            return result;
        }).catch((error) => {
            notifyError(t('poll.shareLowerError'));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to stop the selected question
    // returns "success"
    function stopQuestion(pollKey, uniqueCode, serialNo, pollType=POLL_TYPE.MERGED_POLL, isEndPoll=false) {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
            serialNo: serialNo,
            isEndPoll: isEndPoll
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/stop`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            if (isEndPoll) {
                notifySuccess(t('poll.endSuccess'));
            } else {
                notifySuccess(pollType === POLL_TYPE.ATTENDANCE ? t('attendance.endSuccess') : t('questionBuilder.stopQuestionSuccess', { serialNo }));
            }

            return result;
        }).catch((error) => {
            if (isEndPoll) {
                notifyError(t('poll.endError'));
            } else {
                notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('attendance.endError') : t('questionBuilder.stopQuestionError', { serialNo }));
            }

            console.log(error);
        }).finally(() => {
            setLoading(false);
        })
    }
    
    // Function to stop the selected question
    // returns "success"
    function stopQuestionPresentation(pollKey, uniqueCode, serialNo, pollType, token='') {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
            serialNo: serialNo
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/stop`,
            body: bodyObj,
            token: token
        }).then((result) => {
            console.log(result);
            return result;
        }).catch((error) => {
            notifyError(pollType === POLL_TYPE.ATTENDANCE ? t('attendance.endError') : t('questionBuilder.stopQuestionError', { serialNo }));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        })
    }
    

    function stopAllQuestions(pollKey, uniqueCode) {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/stop-all`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(t('poll.endSuccess'));
            return result;
        }).catch((error) => {
            notifyError(t('poll.endError'));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }


    function stopScheduledPoll(pollKey, uniqueCode) {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/stop-scheduled`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(t('poll.endSuccess'));
            return result;
        }).catch((error) => {
            notifyError(t('poll.endError'));
            console.log(error.code);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to stop the selected question
    // returns "success"
    function lockQuestion(pollKey, uniqueCode, serialNo) {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
            serialNo: serialNo
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/lock`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(t('questionBuilder.lockQuestionSuccess', { serialNo }));
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.lockQuestionError', { serialNo }));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to pause the selected question
    // returns "success"
    function pauseQuestion(pollKey, uniqueCode, serialNo, timeLeft) {
        const bodyObj = {
            pollKey: pollKey,
            uniqueCode: uniqueCode,
            serialNo: serialNo,
            timeLeft: timeLeft
        }

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/pause`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(t('questionBuilder.pauseQuestionSuccess', { serialNo }));
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.pauseQuestionError', { serialNo }));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to stop the selected question
    // returns "success"
    function resumeQuestion(pollKey, uniqueCode, currentQue, timeLeft) {
        const {serialNo, timeLimit} = currentQue;
        const bodyObj = {pollKey, uniqueCode, serialNo, timeLimit, timeLeft}

        setLoading(true);
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/question/resume`,
            body: bodyObj
        }).then((result) => {
            console.log(result);
            notifySuccess(t('questionBuilder.resumeQuestionSuccess', { serialNo }));
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.resumeQuestionError', { serialNo }));
            console.log(error.code);
            console.log(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to get all the polls that are owned by the user that is logged in
    // returns a list of polls 
    function getPollsFromUser(silent=false) {
        // console.log("Get Polls From User");
        if (!silent) setLoading(true);
        return fetchWrapper.get({
            url: config.api.poll + "/poll/user",
        }).then((result) => {
            // console.log(result);
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return [];
        }).finally(() => {
            if (!silent) setLoading(false);
        });
    }

    function getDeletedPollsFromUser() {
        // console.log("Get Polls From User");
        return fetchWrapper.get({
            url: config.api.poll + "/poll/user/delete",
        }).then((result) => {
            // console.log(result);
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return [];
        });
    }

    // Function to get all the polls inside a course
    // returns a list of polls 
    function getPollsFromCourse(courseId) {
        console.log("Get Polls Inside Course");
        setLoading(true);
        return fetchWrapper.get({
            url: config.api.poll + "/poll/course/" + courseId,
        }).then((result) => {
            console.log(result);
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return [];
        }).finally(() => {
            setLoading(false);
        });
    }

    // Function to get all attendances inside a course
    // returns a list of polls 
    function getAttendancesFromCourse(courseId) {
        console.log("Get Attendances Inside Course");
        return fetchWrapper.get({
            url: config.api.poll + "/poll/attendance/course/" + courseId,
        }).then((result) => {
            console.log(result);
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return [];
        });
    }

    // Function to get all the active polls inside a course
    // returns a list of polls 
    function getActivePollsFromCourse(courseId, silent=false) {
        console.log("Get Active Polls Inside Course");
        if (!silent) setLoading(true);
        return fetchWrapper.get({
            url: config.api.poll + "/poll/course/activated-polls/" + courseId,
        }).then((result) => {
            console.log(result);
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return [];
        }).finally(() => {
            if (!silent) setLoading(false);
        });
    }

    // Function to get all the pollData from a pollKey,
    // Returns the data of a single poll including questions, pollDesc, pollTitle, owner, etc
    function getPollDataFromKey(pollKey) {
        console.log("Get Poll Data From Key");
        return fetchWrapper.get({
            url: config.api.poll + "/poll/questions/" + pollKey,
        }).then((result) => {
            console.log(result);
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return {};
        });
    }

    // Function to get all the publishedPollData from a pollKey,
    // Returns the data of a single poll including questions, pollDesc, pollTitle, owner, etc
    function getPublishedPollDataFromKey(pollKey, pollCode) {
        console.log("Get Published Poll Data From Key");
        return fetchWrapper.get({
            url: config.api.poll + "/poll/published/questions/" + pollKey + "/" + pollCode,
        }).then((result) => {
            const {startTime,  pollShareMode} = result.poll;

            //not allow to edit the poll when schedule poll is released
            const isReleasedSchedule = pollShareMode === POLL_SHARE_MODE.SCHEDULE
                && (Date.now() - new Date(startTime).getTime()) > 0;

            result.questions.map(q => {
                if (isReleasedSchedule) {
                    q.shareCount = 1;
                }
                return q;
            }).sort((a,b) => {
                return a.serialNo - b.serialNo;
            })
            formatAPIResponseLog("getPublishedPollDataFromKey", result);
            return result;

        }).catch((error) => {
            notifyError(t('poll.retrievePublishedError'));
            formatAPIErrorLog("getPublishedPollDataFromKey", error);
            console.log(error);
            return null;
        });
    }

    // Function to =edit the information of a published scheduled poll from a pollKey,
    // Returns the data of a single poll including questions, pollDesc, pollTitle, owner, etc
    function editPublishedPollDataFromKey(body, pollKey, pollCode) {
        setLoading(true);
        body.shortName = getShortName();
        return fetchWrapper.put({
            url: config.api.poll + "/poll/published/questions/" + pollKey + "/" + pollCode,
            body: body 
        }).then((result) => {
            console.log(result);
            return result;

        }).catch((error) => {
            notifyError(t('poll.updatePublishedError'));
            console.log(error);
            return null;
        }).finally(() => {
            setLoading(false);
        });
    }

    function getPollRecordFromUniqueCode(pollCode) {
        console.log("Get PollRecord from Poll unique Code");
        return fetchWrapper.get({
            url: config.api.poll + "/poll/published/record/" + pollCode,

        }).then((result) => {
            console.log(result);
            formatAPIResponseLog("GetPollRecordFromUniqueCode", result);
            return result;
        }).catch((error) => {
            console.log(error);
            formatAPIErrorLog("GetPollRecordFromUniqueCode", error);
            return null;
        });
    }

    function getActivePollsFromUser() {
        return fetchWrapper.get({
            url: `${config.api.poll}/poll/user/activated-polls`,
        }).then((result) => {
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveActiveError'));
            console.log(error);
            return [];
        });
    }

    /**
     *
     * @param pollKey
     * @param resourceMapping ex: optImg/queImg-queBank-${queBankId}_${originalImageName} -> destImageName
     * @returns {Promise<*>}
     */
    function importBankImage(pollKey, resourceMapping) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/questionBank/import`,
            body: {pollKey, resourceMapping}
        }).then((result) => {
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.duplicateImageError'));
            console.log(error);
            return null;
        });
    }

    // Function to warm up lambda function
    // returns
    function fakeCall() {
        return fetchWrapper.post({
            url: `${config.api.poll}/poll/fake-call`,
        }).then((result) => {
            return result;
        }).catch(() => {
            return null;
        });
    }


    // Function to get all the polls that are owned by the user that is logged in
    // returns a list of polls
    function getPollsPPT() {
        return fetchWrapper.get({
            url: config.api.poll + "/poll/user",
        }).then((result) => {
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return [];
        })
    }

    function updatePollDetails(pollKey, pollCode, body, silent=false) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/update/${pollKey}/${pollCode}`,
            body: body
        }).then((result) => {
            if (!silent) {
                notifySuccess(t('activity.updateDetailsSuccess'));
            }
            return result;
        }).catch((error) => {
            if (!silent) {
                notifyError(t('activity.updateDetailsError'));
            }
            console.log(error);
            return [];
        });
    }

    function updateQuestionDetails(pollKey, pollCode, serialNo, body) {
        return fetchWrapper.put({
            url: `${config.api.poll}/poll/update/${pollKey}/${pollCode}/${serialNo}`,
            body: body
        }).then((result) => {
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.updateDetailsError'));
            console.log(error);
            return [];
        });
    }

    function addQuestionToLivePoll(pollKey, pollCode, body) {
        return fetchWrapper.post({
            url: `${config.api.poll}/poll/add/${pollKey}/${pollCode}`,
            body: body
        }).then((result) => {
            return result;
        }).catch((error) => {
            notifyError(t('questionBuilder.addIncompleteError'));
            console.log(error);
            return null;
        });
    }

    function getPollDataFromKeyPPT(pollKey, token) {
        const headersObj = {Authorization: `${AUTHORIZATION_TYPE} ${token}`};
        return fetchWrapper.get({
            url: config.api.poll + "/poll/questions/" + pollKey,
            headers: headersObj,
            authorizeRequired: false
        }).then((result) => {
            return result;
        }).catch((error) => {
            notifyError(t('poll.retrieveError'));
            console.log(error);
            return {};
        });
    }

    // function notifySchedule(pollKey, pollCode, body) {
    //     return fetchWrapper.post({
    //         url: `${config.api.poll}/poll/notify/${pollKey}/${pollCode}`,
    //         body: body
    //     }).then((result) => {
    //         console.log(result);
    //         return result;
    //     }).catch((error) => {
    //         console.log(error);
    //         return null;
    //     });
    // }
}